// Login.js
import React, { useContext, useEffect, useState } from "react";
import classes from "./Login.module.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import useLanguage from "../utils/useLanguage";
import CustomInput from "./CustomInput";
import logo from "../assets/logo.png";
import { UserAuth } from "../utils/AuthContext";
import ErrorBox from "../components/ErrorBox";
import ErrorMessage from "../components/ErrorMessage";
import InputLabel from "../components/InputLabel";
import InformationBox from "../components/InformationBox";
import Modal from "../components/Modal";
import CustomButton from "../components/CustomButton";
import Loader from "./Loader";
import PasswordInput from "./PasswordInput";

const Login = ({
  isAuthenticated,
  isAuthenticatedAdmin,
  setIsAuthenticated,
}) => {
  const { currentDirection } = useLanguage();
  // Determine the language based on currentDirection
  const language = currentDirection === "rtl" ? "ar" : "en";

  const userData = useContext(UserAuth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [serverErrorState, setServerErrorState] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [sendLoader, setSendLoader] = useState(false);

  const [forgetPasswordError, setForgetPasswordError] = useState(false);
  const [forgetPasswordErrorMessage, setForgetPasswordErrorMessage] =
    useState("");
  const [forgetPassword, setForgetPassword] = useState(false);
  const [pageTwo, setPageTwo] = useState({ active: false });

  const navigate = useNavigate();
  const { t } = useTranslation("general");

  useEffect(() => {
    if (isAuthenticated || isAuthenticatedAdmin) {
      handleRedirect();
    }
  }, []);

  const handleRedirect = async () => {
    const userType = localStorage.getItem("userType");
    const organizationType = localStorage.getItem("organizationType");
    if (userType === "admin" || userType === "superadmin") {
      navigate("/iraqi-banks-page");
      return;
    }

    if (userType === "organization" || userType === "orgUser") {
      navigateToDashboard(organizationType);
    }

    if (userType === "indi") {
      navigate("/invoiceManagement");
    }

    return;
  };

  const handleGoToGegister = async () => {
    navigate("/register");
  };

  const navigateToDashboard = (organizationType) => {
    switch (organizationType) {
      case "CLINIC":
      case "PHARMACY":
        navigate("/appointments");
        break;
      case "MERCHANT":
        navigate("/invoicesOfShippedContainers");
        break;
      case "NO_CUSTOMER_MERCHANT":
        navigate("/noCustomerInvoices");
        break;
      case "SUPERMARKET":
        navigate("/cashier");
        break;
      case "FABRICSTORE":
        navigate("/rulers");
        break;
      case "PAPERFACTORY":
        navigate("/manage-paper-rulers");
        break;
      case "SCHOOL":
        navigate("/students");
        break;
      case "BANK":
        navigate("/banks-invoices-applications");
        break;
      case "SQUAD":
        navigate("/calculate-attendance");
        break;
      default:
        navigate("/invoiceManagement");
        break;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    // Validate input fields
    if (!email || !password) {
      setError(true);
      setErrorMessage(false);
      setServerErrorState(false);
      return;
    }

    // Email validation regex
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError(false);
      setServerErrorState(false);
      setErrorMessage(true);
      return;
    }

    setError(false);
    setErrorMessage(false);
    setServerErrorState(false);

    try {
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_URL}/login`, {
        email,
        password,
      });

      if (!response) {
        setLoading(false);
        return console.error("Failed to login");
      }

      const { data } = response;
      if (!data) {
        console.error("No data returned from the server");
        return;
      }

      const { token, user, organization, userType, role, specialty } =
        response.data;

      // Store user or organization data based on userType
      if (userType === "organization") {
        const subCompany = response?.data?.subCompany;

        localStorage.setItem("token", token);
        localStorage.setItem("userType", "organization");
        localStorage.setItem("organizationId", organization.id);
        localStorage.setItem(
          "organizationName",
          organization?.organizationName,
        );
        localStorage.setItem("username", organization.organizationName);
        localStorage.setItem("organizationType", organization.organizationType);
        localStorage.setItem("role", "");
        localStorage.setItem("specialty", "");
        localStorage.setItem("orgPhoneNumber", organization.orgPhoneNumber);
        localStorage.setItem("orgEmail", organization.email);
        localStorage.setItem("invoiceType", organization.invoiceType);
        localStorage.setItem(
          "subscriptionType",
          organization.orgSubscriptionType,
        );
        localStorage.setItem("specialOrg", organization.specialOrg);

        // localStorage.setItem("organizationType", "Clinic");
        localStorage.setItem("subCompany", JSON.stringify(subCompany));

        // setUserData({ token, user, organization, userType });
        setIsAuthenticated(token);

        navigateToDashboard(organization.organizationType);
      } else {
        localStorage.setItem("token", token);
        localStorage.setItem("userType", userType);
        localStorage.setItem("userId", user.id);
        // localStorage.setItem("supplierId", user?.supplierId);
        // localStorage.setItem("customerId", user?.customerId);
        localStorage.setItem("email", user.email);
        localStorage.setItem("username", user.username);
        localStorage.setItem("phoneNumber", user?.phoneNumber);
        localStorage.setItem("role", user.role);
        localStorage.setItem("specialty", user.specialty);
        localStorage.setItem("address", user.address);
        localStorage.setItem("invoiceType", user.invoiceType);

        // setUserData({ token, user, userType });
        setIsAuthenticated(token);

        if (userType === "orgUser") {
          localStorage.setItem("organizationId", user?.organizationId);
          localStorage.setItem("organizationType", user?.organizationType);
          localStorage.setItem("organizationName", user?.organizationName);
          localStorage.setItem(
            "subCompany",
            JSON.stringify({
              id: user?.subCompanyId,
              name: user?.subCompanyName,
              nameInAr: user?.subCompanyNameInAr,
            }),
          );

          if (user.role === "Doctor" || user.role === "Healer") {
            navigate("/appointments");
          } else {
            navigateToDashboard(user?.organizationType);
          }

          // navigate(`/${userData?.organizationType?.toLowerCase()}-dashboard`);
        } else {
          navigate("/invoiceManagement");
        }
      }
    } catch (error) {
      const serverErrorRespone = error.response.data.error;
      setServerErrorMessage(t(serverErrorRespone));
      setServerErrorState(true);
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPassword = async () => {
    if (!email) {
      setForgetPasswordError(true);
      setForgetPasswordErrorMessage("emailInputFieldCannotBeEmpty");
      return;
    }

    // Email validation regex
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setForgetPasswordError(true);
      setForgetPasswordErrorMessage("invalidEmail");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/forgetPassword`,
        { email, language },
      );

      if (res?.data) {
        setForgetPasswordError(false);
        setForgetPasswordErrorMessage("");
        setPageTwo((prev) => ({ ...prev, active: true }));
      } else {
        setForgetPasswordError(true);
        setForgetPasswordErrorMessage("passwordNotChanged");
      }
    } catch (err) {
      setForgetPasswordError(true);
      setForgetPasswordErrorMessage(
        err?.response?.data?.message || "passwordNotChanged",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForgetPasswordModal = async () => {
    setForgetPassword(false);
    setForgetPasswordError(false);
    setForgetPasswordErrorMessage("");
  };

  // if (loading) {
  //   return <Loader fullHeight={true} />;
  // }

  return (
    <div className="onboarding-container">
      <div className="onboarding">
        <div className="main-wrapper">
          <div className="language-switcher-container">
            <div className="language-switcher">
              <LanguageSwitcher />
            </div>
          </div>
          <div>
            <img src={logo} alt="Ehsibly" className="website-logo" />
            <p className="intro-text">
              <span className="bold-text">{t("websiteTitle")} </span>
              {t("websiteDescription")}
            </p>
          </div>

          <form className="rigister-login-form o-column " id="login-form">
            <h2>{t("login")}</h2>

            {error && <ErrorBox>{t("emptyInputFields")}</ErrorBox>}
            {errorMessage && <ErrorBox>{t("invalidEmail")}</ErrorBox>}
            {serverErrorState && <ErrorBox>{serverErrorMessage}</ErrorBox>}

            <div>
              <InputLabel title={t("email")} />
              {errorMessage && !error && (
                <ErrorMessage>{t("invalidEmail")}</ErrorMessage>
              )}
              <CustomInput
                placeholder={t("email")}
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                errorText={
                  errorMessage
                    ? t("invalidEmail")
                    : error && !email
                      ? t("inputFieldCannotBeEmpty")
                      : ""
                }
              />
            </div>

            <div>
              <InputLabel title={t("password")} />
              <PasswordInput
                placeholder={t("password")}
                value={password}
                type="password"
                onChange={(e) => setPassword(e.target.value)}
                errorText={
                  error && !password ? t("inputFieldCannotBeEmpty") : ""
                }
              />
            </div>

            {error && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ErrorMessage>{t("emptyInputFields")}</ErrorMessage>
              </div>
            )}

            {errorMessage && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ErrorMessage>{t("invalidEmail")}</ErrorMessage>
              </div>
            )}

            <button
              id="forgot-password-button"
              className={classes.forgetPasswordTextButton}
              type="button"
              onClick={() => setForgetPassword(true)}
            >
              {t("forgetPassword")}
            </button>

            {serverErrorMessage && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ErrorMessage>{serverErrorMessage}</ErrorMessage>
              </div>
            )}

            {loading && <Loader />}
            <button type="submit" onClick={handleLogin} disabled={loading}>
              {t("login")}
            </button>

            {forgetPassword && (
              <Modal
                pageTwo={pageTwo}
                setPageTwo={setPageTwo}
                className={classes.modal}
                noBack={true}
                onClose={() => {
                  handleCloseForgetPasswordModal();
                }}
              >
                {pageTwo?.active ? (
                  <>
                    <h2 className={classes.modalTitle}>{t("done")}</h2>
                    <InformationBox>
                      {t("resetPasswordLinkSent")}
                    </InformationBox>
                  </>
                ) : (
                  <>
                    <h2 className={classes.modalTitle}>
                      {t("forgetPassword")}
                    </h2>
                    {forgetPasswordError && (
                      <ErrorBox>{t(forgetPasswordErrorMessage)}</ErrorBox>
                    )}
                    <h4 className={classes.modalSubTitle}>
                      {t("enterYourEmail")}
                    </h4>
                    <div className={classes.inputContainer}>
                      <label className={classes.inputLabel} htmlFor="email">
                        {t("email")}:
                      </label>

                      {forgetPasswordError && (
                        <div className={classes.centeredError}>
                          <ErrorMessage>
                            {t(forgetPasswordErrorMessage)}
                          </ErrorMessage>
                        </div>
                      )}

                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="o-row o-row--horizontal-align_center u-width_full-width">
                      {loading ? <Loader /> : null}
                    </div>
                    <div className="o-row o-row--horizontal-align_center u-width_full-width">
                      <CustomButton
                        title={t("send")}
                        onClick={(e) => {
                          e.preventDefault(); // Prevent default form behavior
                          handleForgetPassword();
                        }}
                        disabled={loading ? true : false}
                        classList={"u-width_full-width"}
                      />
                    </div>
                  </>
                )}
              </Modal>
            )}

            <p className="login-or-register">{t("or")}</p>

            <button type="button" onClick={handleGoToGegister}>
              {t("register")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
